import { Button } from "@mdotm/shadcn/mdotm/components";
import { ActionText, ScrollWrapper, Text } from "@mdotm/shadcn/legacy/components";
import { FeedbacksControllerApiFactory } from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import { FeedbackSectionIconAlt } from "./icons/FeedbackSectionIconAlt";
import { SphereAIPanelLane } from "./SphereAIPanelLane";
import { SmartTextArea } from "./inputs/SmartTextArea";
import { useState } from "react";
import { useAsync } from "@mdotm/utils/promise";
import { unpromisify } from "@mdotm/shadcn/legacy/utils";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { type FeedbackFaceType, FeedbackFace, FeedbackFaceTypes } from "./icons/FeedbackFaces";

export function SphereAIFeedbackSection(): React.JSX.Element {
	// One point per face, saddest to happiest: the export shows this number as the rating.
	const feedbackMap = {
		negative: 1,
		neutral: 2,
		positive: 3,
		veryPositive: 4,
	};
	const feedbacksV2Api = useApiGen(FeedbacksControllerApiFactory);
	const [feedbackText, setFeedbackText] = useState("");
	const [selectedFeedbackFaceType, setSelectedFeedbackFaceType] = useState<FeedbackFaceType | null>(null);
	const [showThankYou, setShowThankYou] = useState(false);
	const [doSubmit, sendingState] = useAsync(async () => {
		const currentFeedbackText = feedbackText;
		// The rating is the one required field (see `canSubmit`), so this is a type guard rather
		// than a reachable state — Send is disabled until a face is picked.
		if (!selectedFeedbackFaceType) {
			return;
		}
		try {
			setFeedbackText("");
			await feedbacksV2Api.addFeedback({
				rating: feedbackMap[selectedFeedbackFaceType],
				message: feedbackText,
				area: "",
			});
			// Clear the rating too, not just the note: "express another one" must come back to an
			// empty form, otherwise the previous face is still selected and Send is live on a
			// feedback the user hasn't written yet.
			setSelectedFeedbackFaceType(null);
			setShowThankYou(true);
		} catch (err) {
			setFeedbackText(currentFeedbackText);
			throw err;
		}
	});

	// A rating alone is a complete feedback: the face is the low-cost signal we actually want, the
	// written note is the optional extra. So Send follows the FACE, not the text — the old wiring
	// (enabled by text, blocked by the missing face) let the user press Send and get nothing.
	const canSubmit = selectedFeedbackFaceType != null && sendingState.kind !== "loading";

	return (
		<div className="h-full w-full flex flex-col bg-white">
			<ScrollWrapper className="flex-1">
				{/* Identity zone and form share the panel's lane, so Feedback lands where the Chat and
				    History content sits rather than stretching the whole width of a full-screen panel —
				    see SphereAIPanelLane. */}
				<SphereAIPanelLane>
					{/* Identity zone: illustration → title → subtitle. The illustration sits directly on white —
					    no tile behind it, so it doesn't compete with the tinted form block below. Feedback is
					    always an entry state (a form you land on), which is where the panel keeps its
					    illustrations — the Chat dropped its own only because a populated conversation follows it. */}
					<div className="flex w-full flex-col items-center px-4 pt-4 pb-6">
						<div className="flex flex-col items-center gap-2 text-center">
							<FeedbackSectionIconAlt className="h-14 w-auto" />
							<Text type="Body/L/Medium" color={themeCSSVars.palette_N700}>
								Feedback
							</Text>
							<Text as="div" type="Body/M/Book" color={themeCSSVars.palette_N400} className="max-w-xs">
								Your input will be invaluable to us in building the future version
							</Text>
						</div>
					</div>
					{/* The identity zone above already carries its own bottom padding, so the form block only
					    needs its side/bottom gutter — not the extra top inset it had when the zone had none. */}
					<div className="px-4 pb-4">
						{/* Neutral N50 tint: the form is a surface, not an accent, and the blueberry it used to
						    carry was the only blue block in the panel. */}
						<div className="p-4 rounded-lg space-y-2 bg-[var(--palette-N50)]">
							{!showThankYou ? (
								<>
									<Text type="Body/L/Book" as="div" className="mb-2 text-center">
										How interesting and useful do you find this function?
									</Text>
									<div className="flex justify-center">
										<div className="flex items-center">
											{FeedbackFaceTypes.map((type) => (
												<Button
													key={type}
													unstyled
													aria-label={`Rating ${feedbackMap[type]} of ${FeedbackFaceTypes.length}`}
													aria-pressed={selectedFeedbackFaceType === type}
													onClick={() => setSelectedFeedbackFaceType(type)}
												>
													<FeedbackFace type={type} active={selectedFeedbackFaceType === type} />
												</Button>
											))}
										</div>
									</div>
									<hr />
									<Text as="div" className="mb-1 text-center" type="Body/L/Book">
										What insights would you like to find? (optional)
									</Text>
									<div className="relative z-0 min-w-0 grow">
										<SmartTextArea
											onChangeText={setFeedbackText}
											value={feedbackText}
											onEnter={canSubmit ? unpromisify(() => doSubmit()) : undefined}
										/>
									</div>
									{sendingState.kind === "error" && (
										<Text as="div" role="alert" type="Body/M/Book" color={themeCSSVars.palette_D500}>
											Your feedback couldn't be sent. Please try again.
										</Text>
									)}
									{/* Send sits BELOW the field, as a normal platform button: with the note optional, a
									    send glyph floating inside an empty textarea read as "type something first". */}
									<div className="flex justify-end pt-1">
										<Button
											size="small"
											palette="primary"
											disabled={!canSubmit}
											onClick={unpromisify(() => doSubmit())}
										>
											{sendingState.kind === "loading" ? "Sending…" : "Send"}
										</Button>
									</div>
								</>
							) : (
								/* No illustration here: the section already opens with one, and repeating the motif
								   inside the confirmation made the block taller than the form it replaced. */
								<div className="flex flex-col items-center gap-1 py-2 text-center">
									<Text as="div" type="Body/L/Book">
										Thank you for your feedback, it is valuable.
									</Text>
									<ActionText onClick={() => setShowThankYou(false)}>Would you like to express another one?</ActionText>
								</div>
							)}
						</div>
					</div>
				</SphereAIPanelLane>
			</ScrollWrapper>
		</div>
	);
}
